import type { AxiosResponse } from "axios";
import type { ThunkResult } from "src/app/reducers/IMainAppState";
import { OFFER_API_PATH, UTILS_API_PATH } from "src/confs/APIConfig";
import FollowUsOption from "src/offers/objects/FollowUsOption";
import LandingPageTemplate from "src/offers/objects/LandingPageTemplate";
import Offer from "src/offers/objects/Offer";
import type { OfferAction } from "src/offers/objects/OfferAction";
import {
  APIGetAxios,
  APIPostAxios,
  APIPutAxios,
  TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
} from "src/shared/actions/FetchActions";
import { GetPathWithParams } from "src/utilities/URLParamsUtils";

export function GetOffers(
  merchantUUID?: string,
  offerUUID?: string,
): ThunkResult<Promise<Offer[]>> {
  return async (dispatch) => {
    const params: { [key: string]: string } = {};
    if (merchantUUID) {
      params.merchantUUID = merchantUUID;
    }
    if (offerUUID) {
      params.offerUUID = offerUUID;
    }
    const fetchResult: AxiosResponse = await dispatch(
      APIGetAxios(
        GetPathWithParams(OFFER_API_PATH, params),
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return fetchResult.data.map((jsonObj: object) => new Offer(jsonObj));
  };
}

export function CreateOffer(
  offer: Offer,
): ThunkResult<Promise<{ offerUUID: string; offerStaticUrl: string }>> {
  return async (dispatch) => {
    const fetchResult: AxiosResponse = await dispatch(
      APIPostAxios(`${OFFER_API_PATH}`, offer.toJSON()),
    );
    const response: { offerUUID: string; offerStaticUrl: string } =
      fetchResult.data;
    return response;
  };
}

export function EditOffer(offer: Offer): ThunkResult<Promise<void>> {
  return async (dispatch) => {
    await dispatch(
      APIPutAxios(
        `${OFFER_API_PATH}/${offer.offerUUID}`,
        offer.toJSON(),
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return;
  };
}

export function getOfferActions(
  countryCode: string,
): ThunkResult<Promise<OfferAction[]>> {
  return async (dispatch) => {
    const fetchResult: AxiosResponse = await dispatch(
      APIGetAxios(
        `${UTILS_API_PATH}/offer-actions/${countryCode}`,
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return fetchResult.data;
  };
}

export function GetLandingPageTemplates(
  countryCode: string,
): ThunkResult<Promise<LandingPageTemplate[]>> {
  return async (dispatch) => {
    const fetchResult: AxiosResponse = await dispatch(
      APIGetAxios(
        `${UTILS_API_PATH}/lp-templates/${countryCode}`,
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return fetchResult.data.map((landingPageTemplateJSON) => {
      return new LandingPageTemplate(landingPageTemplateJSON);
    });
  };
}

export function UploadOfferLogo(
  logoFile: File,
  onUploadProgress?: (progressEvent: ProgressEvent) => void,
): ThunkResult<Promise<{ utid: string; logoUrl: string }>> {
  return async (dispatch) => {
    const formData: FormData = new FormData();
    formData.append("file", logoFile);

    const response: AxiosResponse<{ utid: string; logoUrl: string }> =
      await dispatch(
        APIPostAxios<{ utid: string; logoUrl: string }>(
          `${OFFER_API_PATH}/action/_logo`,
          formData,
          {
            onUploadProgress,
          },
        ),
      );
    return response.data;
  };
}

export function GetFollowUsOptions(): ThunkResult<Promise<FollowUsOption[]>> {
  return async (dispatch) => {
    const fetchResult: AxiosResponse = await dispatch(
      APIGetAxios(
        `${UTILS_API_PATH}/follow-us`,
        undefined,
        undefined,
        TEMP_AXIOS_NEW_ERROR_CODE_HANDLING,
      ),
    );
    return fetchResult.data.map((followUsOptionJson: any) => {
      return new FollowUsOption(followUsOptionJson);
    });
  };
}
